import { useEffect, useState } from "react";
import { translate, type AppLocale } from "../i18n";
import { BRAND } from "../lib/brand";
import { CheckoutApiError, getOrderStatus } from "../lib/api";
import { apiReturnUrl } from "../lib/return-url";

/** True when only the assigned provider's country closed (delivery country still open). */
export function isProviderCountryClosed(error: unknown): boolean {
  return error instanceof CheckoutApiError && error.code === "country_closed" && error.reason === "provider_country_closed";
}

/**
 * The registry-derived storefront return for an order whose checkout page
 * failed. A return already carried by a loaded summary wins; otherwise, while
 * `failed`, the lightweight order status read is tried once for the same
 * order-access token. Any failure (expired, revoked, unknown token, network)
 * leaves it null so callers fall back to the brand site.
 */
export function useFailureReturnUrl(token: string, failed: boolean, known?: string | null): string | null {
  const knownUrl = apiReturnUrl(known);
  const [fetched, setFetched] = useState<string | null>(null);
  useEffect(() => {
    if (!failed || knownUrl) return;
    const controller = new AbortController();
    void getOrderStatus(token, controller.signal)
      .then(status => { if (!controller.signal.aborted) setFetched(apiReturnUrl(status.returnUrl)); })
      .catch(() => { /* No order return available: keep the brand fallback. */ });
    return () => controller.abort();
  }, [token, failed, knownUrl]);
  return knownUrl ?? (failed ? fetched : null);
}

/** Shared unpaid-checkout closure state, including a usable route to support. */
export function CountryClosed({ locale, providerClosed = false, returnUrl }: {
  locale: AppLocale; providerClosed?: boolean; returnUrl?: string | null;
}) {
  return <main className="co-state">
    <h1>{translate(locale, "cancelled.title")}</h1>
    <p role="alert">{translate(locale, providerClosed ? "error.providerCountryClosed" : "error.countryClosed")}</p>
    <div className="co-stack">
      <a className="co-cta" href={apiReturnUrl(returnUrl) ?? BRAND.marketingUrl}>{translate(locale, "error.backToSite")}</a>
      <a className="co-btn-secondary" href={`mailto:${BRAND.supportEmail}`}>{translate(locale, "cancelled.help")}</a>
    </div>
  </main>;
}
